import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import AdminNavbar from '../components/AdminNavbar';
import { uploadImageToR2 } from '../utils/s3Upload';
import './AdminStaff.css';
import './AdminGallery.css'; // Re-use the premium UI variables

export default function AdminStaff() {
    const [staff, setStaff] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        designation: '',
        quote: '',
        department: 'management',
        parentId: ''
    });
    const [file, setFile] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const fileInputRef = useRef(null);

    const fetchStaff = async () => {
        try {
            const { data, error } = await supabase
                .from('staff')
                .select('*')
                .order('created_at', { ascending: true });
            
            if (error) throw error;
            setStaff(data || []);
        } catch (error) {
            console.error("Error fetching staff", error);
        }
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file && !existingImage) {
            setMessage({ type: 'danger', text: 'Please attach a profile photo.' });
            return;
        }

        try {
            setLoading(true);
            setMessage({ type: '', text: editingId ? 'Updating staff profile...' : 'Pushing to database...' });
            
            let imageUrl = existingImage;
            if (file) {
                imageUrl = await uploadImageToR2(file);
            }

            const staffData = {
                name: formData.name,
                designation: formData.designation,
                quote: formData.quote,
                department: formData.department.toLowerCase(),
                parent_id: formData.parentId || null,
                image: imageUrl
            };
            
            if (editingId) {
                const { error } = await supabase
                    .from('staff')
                    .update(staffData)
                    .eq('id', editingId);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('staff')
                    .insert([staffData]);
                if (error) throw error;
            }
            
            setMessage({ type: 'success', text: editingId ? 'Staff profile updated!' : 'Staff profile created!' });
            cancelEdit();
            fetchStaff();
        } catch (error) {
            setMessage({ type: 'danger', text: `Operation failed: ${error.message}` });
        } finally {
            setLoading(false);
        }
    }

    const handleEdit = (s) => {
        setEditingId(s.id);
        setFormData({
            name: s.name || '',
            designation: s.designation || '',
            quote: s.quote || '',
            department: s.department || 'management',
            parentId: s.parent_id || ''
        });
        setExistingImage(s.image);
        setFile(null);
        if(fileInputRef.current) fileInputRef.current.value = "";
    }

    const cancelEdit = () => {
        setEditingId(null);
        setExistingImage(null);
        setFile(null);
        setFormData({ name: '', designation: '', quote: '', department: 'management', parentId: '' });
        if(fileInputRef.current) fileInputRef.current.value = "";
    }

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you absolutely sure you want to remove ${name} from the database?`)) return;
        
        try {
            const { error } = await supabase
                .from('staff')
                .delete()
                .eq('id', id);
            if (error) throw error;
            setMessage({ type: 'success', text: `${name} has been deleted.` });
            fetchStaff();
        } catch(e) {
            setMessage({ type: 'danger', text: 'Failed to delete staff member.' });
        }
    }

    return (
        <div className="admin-gallery-container pt-5 pb-5">
            <div className="container">
                <AdminNavbar />
                
                {message.text && (
                    <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                        {message.text}
                        <button type="button" className="close" onClick={() => setMessage({type: '', text: ''})} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}

                <div className="row">
                    {/* Add Staff Form */}
                    <div className="col-lg-4 mb-4">
                        <div className="admin-card upload-card">
                            <h4>{editingId ? 'Edit Leader' : 'Add New Leader'}</h4>
                            <p className="text-muted" style={{fontSize: '0.9rem'}}>{editingId ? 'Update the details for this staff member.' : 'Assign a new member to the Flowing Flex Hierarchy.'}</p>
                            
                            <form onSubmit={handleSubmit}>
                                <div className="form-group mt-3">
                                    <label>Full Name</label>
                                    <input type="text" name="name" className="form-control admin-input" value={formData.name} onChange={handleChange} placeholder="e.g. Dr. Sarah Mitchell" required />
                                </div>
                                <div className="form-group mt-3">
                                    <label>Role / Designation</label>
                                    <input type="text" name="designation" className="form-control admin-input" value={formData.designation} onChange={handleChange} placeholder="e.g. Vice Principal" required />
                                </div>
                                <div className="form-group mt-3">
                                    <label>Department</label>
                                    <select name="department" className="form-control admin-input" value={formData.department} onChange={handleChange}>
                                        <option value="management">Board / Management</option>
                                        <option value="academics">Academics Faculty</option>
                                    </select>
                                </div>
                                <div className="form-group mt-3">
                                    <label>Hierarchy Parent (Reports To)</label>
                                    <select name="parentId" className="form-control admin-input" value={formData.parentId} onChange={handleChange}>
                                        <option value="">-- No Parent (Root / Top Level) --</option>
                                        {staff.filter(s => s.department === formData.department && s.id !== editingId).map(s => (
                                            <option key={s.id} value={s.id}>{s.name} ({s.designation})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group mt-3">
                                    <label>Inspirational Quote (Optional)</label>
                                    <textarea name="quote" className="form-control admin-input" rows="2" value={formData.quote} onChange={handleChange} placeholder='"Excellence in all we do."' />
                                </div>
                                <div className="form-group mt-4">
                                    <label>Profile Image</label>
                                    {existingImage && (
                                        <div className="mb-2">
                                            <img src={existingImage} alt="Current" style={{width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover'}} />
                                            <span className="ms-2 text-muted" style={{fontSize: '0.85rem'}}>Current Image</span>
                                        </div>
                                    )}
                                    <div className="file-drop-area" style={{padding: '30px 15px'}}>
                                        <input type="file" className="file-input" accept="image/*" onChange={(e) => setFile(e.target.files[0])} ref={fileInputRef} required={!existingImage} />
                                        <div className="file-msg text-muted">
                                            {file ? <span className="text-success fw-bold">✓ {file.name}</span> : <span>Click to attach photo<br/><small>(PNG, JPG)</small></span>}
                                        </div>
                                    </div>
                                </div>

                                <button type="submit" className="upload-btn w-100 mt-4" disabled={loading}>
                                    {loading ? 'Processing...' : editingId ? 'Update Profile' : 'Register to Hierarchy'}
                                </button>
                                {editingId && (
                                    <button type="button" onClick={cancelEdit} className="btn btn-secondary w-100 mt-2 font-weight-bold" disabled={loading}>
                                        Cancel Edit
                                    </button>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* Staff Data Table */}
                    <div className="col-lg-8">
                        <div className="admin-card">
                            <h4>Current Master Database</h4>
                            <p className="text-muted" style={{fontSize: '0.9rem'}}>Manage the leadership profiles that generate our UI hierarchies.</p>
                            
                            <div className="table-responsive mt-4" style={{maxHeight:'650px', overflowY:'auto'}}>
                                <table className="table table-hover align-middle staff-table">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Avatar</th>
                                            <th>Bio Details</th>
                                            <th>Department</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {staff.length > 0 ? staff.map((s, idx) => (
                                            <tr key={s.id || idx}>
                                                <td style={{width: '75px'}}>
                                                    <img src={s.image} alt={s.name} style={{width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ecf0f1'}} />
                                                </td>
                                                <td>
                                                    <h6 className="mb-0 fw-bold text-dark">{s.name}</h6>
                                                    <small className="text-info fw-bold" style={{textTransform: 'uppercase', letterSpacing: '0.5px'}}>{s.designation}</small>
                                                    {s.quote && <p className="text-muted mb-0 mt-1" style={{fontSize: '0.8rem', fontStyle: 'italic'}}>{s.quote}</p>}
                                                </td>
                                                <td>
                                                    <span className={`badge ${s.department === 'academics' ? 'bg-primary' : 'bg-success'}`} style={{padding: '6px 10px', borderRadius: '8px', textTransform: 'capitalize'}}>
                                                        {s.department}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-2" style={{gap: '8px'}}>
                                                        <button onClick={() => handleEdit(s)} className="btn btn-sm btn-outline-primary" style={{borderRadius: '6px', fontSize: '0.8rem'}}>
                                                            ✏️ Edit
                                                        </button>
                                                        <button onClick={() => handleDelete(s.id, s.name)} className="btn btn-sm btn-outline-danger" style={{borderRadius: '6px', fontSize: '0.8rem'}}>
                                                            🗑️ Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="4" className="text-center py-5"><h5 className="text-muted mb-0">No staff records found in the database.</h5></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
